import { Link, useLocation } from "react-router-dom";
import { Settings2, Tags } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/ui/lib/utils";
import { SETTINGS_URL } from "@/settings/config/settings-urls";
import SettingsResourceTree from "./settings-resource-tree";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore";
import { useGetOrganizations } from "@/auth/api/queries/user.queries";

const SettingsSidebar = () => {
  const { t } = useTranslation("settings");
  const location = useLocation();
  const { selectedOrganizationId } = useOrganizationStore();
  const { data: orgsData } = useGetOrganizations();

  const isOrgActive = location.pathname === SETTINGS_URL.ORGANIZATION;
  const isPreferencesActive = location.pathname === SETTINGS_URL.PREFERENCES;
  const isTaxonomyActive = location.pathname === SETTINGS_URL.TAXONOMY;
  const organizations = orgsData?.data || [];
  const selectedOrg = organizations.find(org => org.id === selectedOrganizationId);

  return (
    <aside className="w-64 border-r bg-background h-full overflow-y-auto">
      <div className="p-4">
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2">{t("sidebar.title")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("sidebar.description")}
          </p>
        </div>

        <nav className="space-y-2">
          {/* Organization Name - Clickable */}
          <Link
            to={SETTINGS_URL.ORGANIZATION}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors",
              isOrgActive
                ? "bg-accent text-accent-foreground font-medium"
                : "text-foreground hover:bg-accent/50 hover:text-accent-foreground"
            )}
          >
            <span className="flex-1 font-medium">
              {selectedOrg?.name || t("sidebar.organization")}
            </span>
          </Link>

          {/* Locations Section - Collapsible with Machines */}
          <SettingsResourceTree />

          {/* Preferences — global app settings (e.g. map provider) */}
          <Link
            to={SETTINGS_URL.PREFERENCES}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors",
              isPreferencesActive
                ? "bg-accent text-accent-foreground font-medium"
                : "text-foreground hover:bg-accent/50 hover:text-accent-foreground",
            )}
          >
            <Settings2 className="h-4 w-4" />
            <span className="flex-1 font-medium">{t("sidebar.preferences")}</span>
          </Link>

          {/* Taxonomy — read-only classification reference */}
          <Link
            to={SETTINGS_URL.TAXONOMY}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors",
              isTaxonomyActive
                ? "bg-accent text-accent-foreground font-medium"
                : "text-foreground hover:bg-accent/50 hover:text-accent-foreground",
            )}
          >
            <Tags className="h-4 w-4" />
            <span className="flex-1 font-medium">{t("sidebar.taxonomy")}</span>
          </Link>
        </nav>
      </div>
    </aside>
  );
};

export default SettingsSidebar;
